import React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/formatters';
import { parseDateInput } from '@/utils/date-operations';
import { Calendar } from 'lucide-react';

interface DateInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'type'
> {
  value: string;
  onChange: (value: string) => void;
  /** Latest selectable ISO date; a later typed date reverts like unparseable input. */
  max?: string;
  ref?: React.Ref<HTMLInputElement>;
}

function DateInput({
  value,
  onChange,
  max,
  className,
  disabled,
  ref,
  ...props
}: DateInputProps) {
  const [typedText, setTypedText] = React.useState(() =>
    value ? formatDate(value) : ''
  );
  const nativePickerRef = React.useRef<HTMLInputElement>(null);

  // Re-sync the visible text when `value` changes from outside the field (calendar
  // pick, form reset, edit-mode hydration). Adjusting state during render - rather
  // than in an effect - avoids a cascading re-render. Typing only updates local
  // state and never changes `value`, so this never fights in-progress input.
  const [lastSyncedValue, setLastSyncedValue] = React.useState(value);
  if (value !== lastSyncedValue) {
    setLastSyncedValue(value);
    setTypedText(formatDate(value));
  }

  const commitTypedText = () => {
    if (typedText.trim() === '') {
      onChange('');
      // A parent that refuses '' may leave `value` unchanged, which skips the
      // re-sync above; show `value` until the parent's new value arrives.
      setTypedText(formatDate(value));
      return;
    }
    const parsedIsoDate = parseDateInput(typedText);
    if (parsedIsoDate && (!max || parsedIsoDate <= max)) {
      onChange(parsedIsoDate);
      setTypedText(formatDate(parsedIsoDate));
    } else {
      // Unparseable or out-of-range input: revert to the last valid value.
      setTypedText(formatDate(value));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitTypedText();
    }
  };

  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value || '');
  };

  const openNativePicker = () => {
    const picker = nativePickerRef.current;
    if (!picker) return;
    picker.focus();
    if (typeof picker.showPicker !== 'function') {
      // Older browsers without showPicker
      picker.click();
      return;
    }
    try {
      picker.showPicker();
    } catch {
      // Some browsers reject showPicker outside a trusted gesture. Focus alone
      // still lets the user drive the field from the keyboard.
    }
  };

  return (
    <div className="relative w-full">
      <Input
        ref={ref}
        type="text"
        value={typedText}
        onChange={(e) => setTypedText(e.target.value)}
        onBlur={commitTypedText}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="dd-mm-yyyy"
        selectAllOnFocus={false}
        className={cn('pr-10', className)}
        {...props}
      />
      <button
        type="button"
        onClick={openNativePicker}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Open calendar"
        className="absolute right-0 top-0 flex h-full items-center px-3 text-gray-400 disabled:cursor-not-allowed"
      >
        <Calendar className="h-4 w-4" />
      </button>
      {/* Hidden native date input: exists only so the calendar button can open
          the browser's date picker. It is never typed into directly, but it is
          focused while the popup is open, so it cannot be aria-hidden. */}
      <input
        ref={nativePickerRef}
        type="date"
        value={value}
        max={max}
        onChange={handlePickerChange}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Pick a date"
        className="pointer-events-none absolute inset-y-0 right-0 w-10 opacity-0"
      />
    </div>
  );
}

DateInput.displayName = 'DateInput';

export { DateInput };
